import { create } from 'zustand';
import { useAuthStore } from './authStore';
import { Todo, TodoList, TodoPriority, TodoStatus } from '../types/todo';

type Scope = 'my' | 'family';

interface FetchTodoParams {
  scope?: Scope;
  status?: TodoStatus;
  listId?: string;
  eventId?: string;
}

interface CreateTodoInput {
  title: string;
  description?: string;
  listId?: string;
  dueDateTime?: Date | null;
  priority?: TodoPriority;
  assigneeId?: string;
  assigneeName?: string;
  isFamily?: boolean;
  eventId?: string;
  eventTitle?: string;
}

interface UpdateTodoInput extends Partial<CreateTodoInput> {
  status?: TodoStatus;
}

interface TodoState {
  todos: Todo[];
  lists: TodoList[];
  isLoading: boolean;
  error: string | null;
  fetchLists: () => Promise<void>;
  fetchTodos: (params?: FetchTodoParams) => Promise<void>;
  createTodo: (input: CreateTodoInput) => Promise<Todo>;
  updateTodo: (todoId: string, input: UpdateTodoInput) => Promise<Todo>;
  deleteTodo: (todoId: string) => Promise<void>;
  createList: (input: { name: string; theme?: string; icon?: string }) => Promise<TodoList>;
  updateList: (listId: string, input: { name?: string; theme?: string; icon?: string }) => Promise<TodoList>;
  deleteList: (listId: string) => Promise<void>;
  clearError: () => void;
}

import { getApiUrl, getApiKey } from '../utils/apiConfig';

// Get API configuration (throws if not configured)
const API_URL = getApiUrl();
const API_KEY = getApiKey();

const getHeaders = (): HeadersInit => {
  const { idToken, isFamilyMode } = useAuthStore.getState();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (API_KEY) {
    headers['X-Api-Key'] = API_KEY;
  }
  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`;
  }
  if (isFamilyMode) {
    headers['X-Family-Mode'] = 'true';
    headers['X-Attribution-Actor'] = 'family';
  }
  return headers;
};

const deserializeTodo = (item: any): Todo => {
  // Parse dueDateTime correctly to avoid timezone shifts
  let dueDateTime: Date | undefined;
  if (item.dueDateTime) {
    const dueStr = item.dueDateTime;
    // Check if it's a date-only string (YYYY-MM-DD) or full ISO string
    if (typeof dueStr === 'string' && dueStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Date-only string: parse as local date
      const [year, month, day] = dueStr.split('-').map(Number);
      dueDateTime = new Date(year, month - 1, day);
    } else {
      // Full ISO string: extract date components from UTC to avoid timezone shifts
      const isoDate = new Date(dueStr);
      // Use UTC methods to get the date components, then create local date
      const year = isoDate.getUTCFullYear();
      const month = isoDate.getUTCMonth();
      const day = isoDate.getUTCDate();
      dueDateTime = new Date(year, month, day);
    }
  }

  return {
    todoId: item.todoId,
    familyId: item.familyId,
    title: item.title,
    description: item.description,
    listId: item.listId,
    dueDateTime,
    status: item.status,
    priority: item.priority,
    assigneeId: item.assigneeId,
    assigneeName: item.assigneeName,
    isFamily: item.isFamily,
    eventId: item.eventId,
    eventTitle: item.eventTitle,
    createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
    updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date(),
    actorType: item.actorType,
    actorId: item.actorId,
  };
};

const deserializeList = (item: any): TodoList => ({
  listId: item.listId,
  familyId: item.familyId,
  name: item.name,
  theme: item.theme,
  icon: item.icon,
  createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
  updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date(),
  actorType: item.actorType,
  actorId: item.actorId,
});

export const useTodoStore = create<TodoState>((set, get) => ({
  todos: [],
  lists: [],
  isLoading: false,
  error: null,

  fetchLists: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(`${API_URL}/todo-lists`, {
        method: 'GET',
        headers: getHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch lists' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }
      const data = await response.json();
      const lists = (data.lists || []).map(deserializeList);
      set({ lists, isLoading: false });
    } catch (error: any) {
      console.error('Error fetching lists:', error);
      set({ error: error.message || 'Failed to fetch lists', isLoading: false });
    }
  },

  fetchTodos: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const search = new URLSearchParams();
      if (params.scope) search.append('scope', params.scope);
      if (params.status) search.append('status', params.status);
      if (params.listId) search.append('listId', params.listId);
      if (params.eventId) search.append('eventId', params.eventId);
      const url = `${API_URL}/todos${search.toString() ? `?${search.toString()}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch todos' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }
      const data = await response.json();
      const todos = (data.todos || []).map(deserializeTodo);
      set({ todos, isLoading: false });
    } catch (error: any) {
      console.error('Error fetching todos:', error);
      set({ error: error.message || 'Failed to fetch todos', isLoading: false });
    }
  },

  createTodo: async (input) => {
    set({ error: null });
    try {
      const payload: any = {
        title: input.title,
        description: input.description,
        listId: input.listId,
        priority: input.priority,
        assigneeId: input.assigneeId,
        // assigneeName is not sent - backend will derive it from assigneeId
        isFamily: input.isFamily,
        eventId: input.eventId,
        eventTitle: input.eventTitle,
      };
      if (input.dueDateTime) {
        payload.dueDateTime = input.dueDateTime.toISOString();
      }

      const response = await fetch(`${API_URL}/todos`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to create todo' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }
      const data = await response.json();
      const todo = deserializeTodo(data.todo);
      set((state) => ({ todos: [todo, ...state.todos] }));
      return todo;
    } catch (error: any) {
      console.error('Error creating todo:', error);
      set({ error: error.message || 'Failed to create todo' });
      throw error;
    }
  },

  updateTodo: async (todoId, input) => {
    set({ error: null });
    
    // Store original todo for rollback on error
    const originalTodo = get().todos.find((t) => t.todoId === todoId);
    
    // Optimistic update
    set((state) => ({
      todos: state.todos.map((t) =>
        t.todoId === todoId 
          ? { ...t, ...input, dueDateTime: input.dueDateTime || t.dueDateTime } 
          : t
      ),
    }));
    
    try {
      const payload: any = { ...input };
      // Remove assigneeName - backend will derive it from assigneeId
      delete payload.assigneeName;
      if (input.dueDateTime) {
        payload.dueDateTime = input.dueDateTime.toISOString();
      }
      const response = await fetch(`${API_URL}/todos/${todoId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to update todo' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }
      const data = await response.json();
      const updated = deserializeTodo(data.todo);
      // Final update with server data
      set((state) => ({
        todos: state.todos.map((t) => (t.todoId === todoId ? updated : t)),
      }));
      return updated;
    } catch (error: any) {
      console.error('Error updating todo:', error);
      // Revert optimistic update on error
      if (originalTodo) {
        set((state) => ({
          todos: state.todos.map((t) => (t.todoId === todoId ? originalTodo : t)),
        }));
      }
      set({ error: error.message || 'Failed to update todo' });
      throw error;
    }
  },

  deleteTodo: async (todoId) => {
    set({ error: null });
    try {
      const response = await fetch(`${API_URL}/todos/${todoId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to delete todo' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }
      set((state) => ({
        todos: state.todos.filter((t) => t.todoId !== todoId),
      }));
    } catch (error: any) {
      console.error('Error deleting todo:', error);
      set({ error: error.message || 'Failed to delete todo' });
      throw error;
    }
  },

  createList: async (input) => {
    set({ error: null });
    try {
      const response = await fetch(`${API_URL}/todo-lists`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(input),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to create list' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }
      const data = await response.json();
      const list = deserializeList(data.list);
      set((state) => ({ lists: [...state.lists, list] }));
      return list;
    } catch (error: any) {
      console.error('Error creating list:', error);
      set({ error: error.message || 'Failed to create list' });
      throw error;
    }
  },

  updateList: async (listId, input) => {
    set({ error: null });
    try {
      const response = await fetch(`${API_URL}/todo-lists/${listId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(input),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to update list' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }
      const data = await response.json();
      const list = deserializeList(data.list);
      set((state) => ({
        lists: state.lists.map((l) => (l.listId === listId ? list : l)),
      }));
      return list;
    } catch (error: any) {
      console.error('Error updating list:', error);
      set({ error: error.message || 'Failed to update list' });
      throw error;
    }
  },

  deleteList: async (listId) => {
    set({ error: null });
    try {
      const response = await fetch(`${API_URL}/todo-lists/${listId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to delete list' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }
      set((state) => ({
        lists: state.lists.filter((l) => l.listId !== listId),
        todos: state.todos.filter((t) => t.listId !== listId),
      }));
    } catch (error: any) {
      console.error('Error deleting list:', error);
      set({ error: error.message || 'Failed to delete list' });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
